import { getApiConfig } from '../config/api';
import { handleUnauthorized } from '../utils/jwtUtils';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { apiService } from './api';
import { downloadBlob } from '../utils/downloadFile';
// import { useAuth } from '../hooks/useAuth';

// Map a file name's extension to a MIME type, used when a Blob has no type set
// (the native side needs a correct mimeType to save the file).
const mimeTypeForFileName = (fileName: string): string => {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'pdf':
      return 'application/pdf';
    case 'png':
      return 'image/png';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'csv':
      return 'text/csv';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'xls':
      return 'application/vnd.ms-excel';
    default:
      return 'application/octet-stream';
  }
};

// Get API configuration
const apiConfig = getApiConfig();
const API_BASE_URL = apiConfig.BASE_URL;
const ENDPOINTS = apiConfig.ENDPOINTS;


// Generic request method for devices
const deviceRequest = async <T>(
  endpoint: string,
  options: any = {}
): Promise<T> => {

  try {
    let isRefreshing = false;
    // Add authorization header if token exists
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {}),
    };

    if (!(options.body instanceof FormData)) {
      // only set JSON when NOT sending FormData
      headers['Content-Type'] = 'application/json';
    }

    // Make HTTP request
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: options.method || 'GET',
      headers,
      body: options.body,
      credentials: 'include',
      mode: 'cors',
    });

    // Handle 401 Unauthorized - clear tokens and redirect to login
    // This handles cases where user account is deleted from another device/session
    if (response.status === 401) {
      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const refreshResult = await apiService.refreshAccessToken();
          if (refreshResult.success) {
            // Retry the original request with new token
            return deviceRequest(endpoint, options);
          } else {
            // Refresh failed, clear tokens and redirect to login
            // This handles account deletion from another device/session
            handleUnauthorized('API Service');
            const result: any = {
              success: false,
              message: 'Session expired. Please login again.',
              error: 'Session expired. Please login again.',
              data: null,
            }
            return result;
          }
        } catch (error) {
          // Account deleted or token invalid - clear everything and redirect
          handleUnauthorized('API Service');
          const result: any = {
            success: false,
            error: 'Authentication failed. Please login again.'
          };
          return result;
        } finally {
          isRefreshing = false;
        }
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (response.status == 204) {
      return null as any;
    }

    const responseData = await response.json();

    // Handle different response structures
    if (responseData === undefined || responseData === null) {
      throw new Error('API returned empty response');
    }

    return responseData.data || responseData;
  } catch (error) {
    throw new Error(`API request failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Device interface (for create/update operations)
export interface Device {
  deviceId: number;
  deviceTypeId: number;
  deviceName: string;
  serialNumber: string;
  deviceLocationType: 'indoor' | 'outdoor';
  installationLocation: string;
  sector: string;
  description: string;
  latitude: number;
  longitude: number;
  deviceImageUrl?: string; // URL to the uploaded image
  isActive: boolean;
  createdAt: string;
  environmentMode?: string;
  onboardingStatus?: string;
  operationalStatus?: string;
  wifiNetworkName?: string;
  lastDataReceived?: string;
  siteId: number | null;
  userId: number | null;
}

export interface CreateDeviceResponse {
  success: boolean;
  message?: string;
  error?: string;
  eventCode?: string;
  deviceDetails?: Device;
}

// Device Dashboard Response interface (matches backend DeviceDashboardDto)
export interface DeviceDashboardDto {
  deviceId: number;
  deviceName?: string;
  deviceModel?: string;
  deviceTypeId?: number;
  serialNumber?: string;
  installationLocation?: string;
  isActive: boolean;
  lastUpdated?: string;
  parameters: Record<number, ParameterDto>;
  latitude?: number;
  longitude?: number;
  deviceImageUrl?: string;
  environmentMode?: string;
  operationalStatus?: string;
  onboardingStatus?: string;
  wifiNetworkName?: string;
  siteId?: number;
}

// Parameter DTO Preferred interface
export interface ParameterDtoPreferred {
  driverID: number;
  parameterName: string;
  parameterDisplayName: string;
  unit: string;
  unitId: number;
}

// Country code interface
export interface CountryCode {
  country: string;
  code: string;
  iso: string;
}

// Device Type interface
export interface DeviceType {
  id: number;
  typeName: string;
  description: string;
}

// Pollutant Parameter interface
export interface PollutantParameter {
  pollutantParameterId: number;
  parameterName: string;
  parameterCode: string;
  displayName: string;
  description?: string;
  unit: string;
  unitSymbol: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

// Parameter DTO interface
export interface ParameterDto {
  value: number;
  subIndex: number;
  parameterName: string;
  parameterDriverId: number;
}

// AQI Info Data interface
export interface AqiInfoData {
  aqiParameters: ParameterDtoPreferred[];
  aqiCategoryData: AqiCategoryData[];
  nonAqiParameters: ParameterDtoPreferred[];
  nonAqiCategoryData: AqiCategoryData[];
  healthParameters: ParameterDtoPreferred[];
  healthCategoryData: AqiCategoryData[];
}
export interface AqiCategoryData {
  category: string;
  color: string;
  description: string;
  values: Record<string, string>;
}

// Device Sensor Data interfaces
export interface DeviceSensorData {
  deviceId: number;
  deviceName: string;
  // deviceLocationType: string;
  serialNumber: string;
  readings: DeviceReading[];
}

export interface DeviceReading {
  id: number;
  parameterId: number;
  parameterDriverId: number;
  parameterName: string;
  parameterDisplayName: string;
  parameterUnit: string;
  parameterValue: number;
  recordedAt: string;
  subIndex: number;
  typeId: number;
  preferredUnitId: number;
  preferredUnitName: string;
  primaryUnitId: number;
  primaryUnitName: string;
}

export interface DeviceDataHeatMap {
  parameterValue: number;
  recordedAt: string;
}

// Create device request interface
export interface CreateDeviceRequest {
  DeviceTypeId: any;
  DeviceName: string;
  SerialNumber: string;
  DeviceLocationType?: 'indoor' | 'outdoor';
  InstallationLocation: string;
  // Sector: string;
  Description: string;
  // IsActive: boolean;
  Latitude: any;
  Longitude: any;
  DeviceImage?: File | null;
  DeviceImageUrl?: string | null;
  EnvironmentMode?: string | null;
  SiteId?: number | null;
}

// Update device request interface
export interface UpdateDeviceRequest {
  DeviceName?: string;
  DeviceLocationType?: 'indoor' | 'outdoor';
  DeviceTypeId?: number;
  SerialNumber?: string;
  InstallationLocation?: string;
  // Sector?: string;
  Description?: string;
  Latitude?: number;
  Longitude?: number;
  DeviceImage?: File | null;
  DeviceImageUrl?: string | null;
  // IsActive?: boolean;
  EnvironmentMode?: string | null;
  SiteId?: number | null;
}

export interface UpdateDeviceStatusRequest {
  serialNumber: string;
  operationalStatus: string;
  onboardingStatus: string;
  connectedWifiNetworkName: string;
}

// public string? DeviceName { get; set; }
// public string? DeviceLocationType { get; set; }
// public int? DeviceTypeId { get; set; }
// public string? InstallationLocation { get; set; }
// public bool? IsActive { get; set; }
// public string? Sector { get; set; }
// public double? Latitude { get; set; }
// public double? Longitude { get; set; }
// public string? Description { get; set; }
// public IFormFile? DeviceImage { get; set; }

// Device list response interface
export interface DeviceListResponse {
  devices: Device[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

//Device Offsets interface
export interface DeviceOffsetsData {
  parameterName: string
  parameterId: number
  offsetX: number | string
  offsetY: number | string
}

export interface UpdateDeviceOffsetsDataRequest {
  parameterId: number
  offsetX: number
  offsetY: number
}

// Device filter interface
export interface DeviceFilter {
  search?: string;
  deviceType?: string;
  status?: string;
  sectorGroup?: string;
  page?: number;
  limit?: number;
}

interface ParameterRecord {
  value: number;
  recordedAt: string;
}

interface SensorParameter {
  min: ParameterRecord;
  max: ParameterRecord;
  unit: string;
}

export interface DeviceDataHeatMapMinMax {
  temperature: SensorParameter;
  humidity: SensorParameter;
  alerts: number;
}

export interface StatsResult {
  min: number;
  max: number;
  avg: number;
  minSlot: string; // ISO string of the 2-hour slot
  maxSlot: string;
}

function floorTo2HourSlot(date: Date): Date {
  const floored = new Date(date);
  const hours = floored.getHours(); // <-- local hours
  floored.setMinutes(0, 0, 0);       // reset minutes/seconds/ms
  floored.setHours(Math.floor(hours / 2) * 2); // floor to 2-hour slot
  return floored;
}

export function calculateStats(data: DeviceDataHeatMap[]): StatsResult | null {
  if (!data || data.length === 0) return null;

  // 1️⃣ Group by 2-hour slots
  const groups = new Map<string, number[]>();

  for (const item of data) {
    const date = new Date(item.recordedAt);
    const slotStart = floorTo2HourSlot(date).toISOString();

    if (!groups.has(slotStart)) {
      groups.set(slotStart, []);
    }
    groups.get(slotStart)!.push(item.parameterValue);
  }

  // 2️⃣ Calculate average for each slot
  const slotAverages: { slot: string; avg: number }[] = [];
  groups.forEach((values, slot) => {
    const sum = values.reduce((a, b) => a + b, 0);
    const avg = sum / values.length;
    slotAverages.push({ slot, avg });
  });

  // 3️⃣ Calculate overall min, max, avg from slot averages
  let min = slotAverages[0].avg;
  let max = slotAverages[0].avg;
  let minSlot = slotAverages[0].slot;
  let maxSlot = slotAverages[0].slot;
  let sum = 0;

  for (const { slot, avg } of slotAverages) {
    if (avg < min) {
      min = avg;
      minSlot = slot;
    }
    if (avg > max) {
      max = avg;
      maxSlot = slot;
    }
    sum += avg;
  }
  const avg = sum / slotAverages.length;
  // console.log(min, minSlot);
  // console.log(max, maxSlot);
  return {
    min: parseFloat(min.toFixed(1)),
    max: parseFloat(max.toFixed(1)),
    avg: parseFloat(avg.toFixed(1)),
    minSlot,
    maxSlot
  };
}

// Helper function to convert canvas to PDF using jsPDF
const canvasToPDF = async (canvas: HTMLCanvasElement, fileName: string): Promise<Blob> => {
  // Import jsPDF dynamically
  // const { default: jsPDF } = await import('jspdf');

  // Create PDF document
  const pdf = new jsPDF({
    orientation: 'landscape', // Use landscape for better chart display
    unit: 'mm',
    format: 'a4'
  });

  // Get canvas dimensions
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;

  // Calculate dimensions to fit the page
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // Calculate scaling to fit the chart on the page
  const scaleX = pageWidth / canvasWidth;
  const scaleY = pageHeight / canvasHeight;
  const scale = Math.min(scaleX, scaleY) * 0.9; // 0.9 for some margin

  const scaledWidth = canvasWidth * scale;
  const scaledHeight = canvasHeight * scale;

  // Center the image on the page
  const x = (pageWidth - scaledWidth) / 2;
  const y = (pageHeight - scaledHeight) / 2;

  // Convert canvas to image data
  const imgData = canvas.toDataURL('image/png', 1.0);

  // Add image to PDF
  pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);

  // Generate PDF blob
  return new Promise((resolve) => {
    pdf.save(fileName, { returnPromise: true });
    // Get the blob from the PDF
    const pdfBlob = pdf.output('blob');
    resolve(pdfBlob);
  });
};

// Helper function to capture chart as image with header
const captureChartAsImage = async (deviceData: any): Promise<HTMLCanvasElement> => {
  // Find the chart container in the DOM
  const chartContainer = document.querySelector('.chart-container') ||
    document.querySelector('.chart-container-heatmap') ||
    document.querySelector('[class*="chart"]');

  const metricsContainer = document.querySelector('.metric-selectors') ||
    document.querySelector('.metric-selectors-heatmap') ||
    document.querySelector('[class*="metric-selectors"]');

  if (!chartContainer) {
    throw new Error('Chart container not found. Please ensure the chart is rendered.');
  }

  if (!metricsContainer) {
    throw new Error('Metrics container not found. Please ensure the metrics are rendered.');
  }

  // Create a wrapper div with header
  const wrapper = document.createElement('div');
  wrapper.style.cssText = `
    background: white;
    padding: 20px;
    font-family: DM Sans, sans-serif;
    display: flex;
    flex-direction: column;
    align-items: center;
  `;

  // Create header section
  const header = document.createElement('div');
  header.style.cssText = `
    text-align: center;
    margin-bottom: 60px;
    width: 100%;
  `;

  // Main title
  const title = document.createElement('h1');
  title.textContent = `${deviceData.deviceName} (${deviceData.serialNumber})`;
  title.style.cssText = `
    font-size: 24px;
    font-weight: bold;
    color: #363636;
    margin: 0 0 10px 0;
  `;

  // Subtitle with date range
  const subtitle = document.createElement('p');
  subtitle.textContent = `From ${new Date(deviceData.fromDate).toLocaleDateString('en-US', options)} to ${new Date(deviceData.toDate).toLocaleDateString('en-US', options)}`;
  subtitle.style.cssText = `
    font-size: 16px;
    color: #666;
    margin: 0;
  `;

  header.appendChild(title);
  header.appendChild(subtitle);

  // Clone the chart container
  const chartClone = chartContainer.cloneNode(true) as HTMLElement;
  chartClone.style.cssText = `
    width: 100%;
    max-width: 1500px;
  `;
  const metricsClone = metricsContainer.cloneNode(true) as HTMLElement;
  metricsClone.style.cssText = `
    width: 100%;
    max-width: 800px;
  `;

  console.log(metricsClone.childNodes);

  // metricsClone.childNodes.forEach((child: any) => {
  //   child.style.cssText = `
  //     padding: 0.4rem 1.5rem;
  //     border: 1px solid #00000033;
  //     border-radius: 10px;
  //     background: white;
  //     color: #363636;
  //     font-size: 0.875rem;
  //     font-weight: 400;
  //     cursor: pointer;
  //     transition: all 0.2s;
  //     min-width: 60px;
  //     text-align: center;
  //   `; 
  // });

  // Create a chart section that includes both chart and metrics
  const chartSection = document.createElement('div');
  chartSection.style.cssText = `
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  `;

  // Add chart to section
  chartSection.appendChild(chartClone);

  // Add metrics below chart but within the same section
  chartSection.appendChild(metricsClone);

  // Assemble the wrapper
  wrapper.appendChild(header);
  wrapper.appendChild(chartSection);
  // Temporarily add to DOM for capture
  wrapper.style.position = 'absolute';
  wrapper.style.left = '-9999px';
  wrapper.style.top = '0';
  document.body.appendChild(wrapper);

  try {
    // Use html2canvas to capture the wrapper with header
    const canvas = await html2canvas(wrapper, {
      backgroundColor: '#ffffff',
      scale: 2, // Higher resolution
      useCORS: true,
      allowTaint: true,
      width: wrapper.offsetWidth,
      height: wrapper.offsetHeight
    });

    return canvas;
  } finally {
    // Clean up
    document.body.removeChild(wrapper);
  }
};

const captureDeviceTableAsImage = async (deviceData: any): Promise<HTMLCanvasElement> => {
  const deviceTableContainer = document.querySelector('.device-view-container') ||
    document.querySelector('[class*="device-view-container"]');
  if (!deviceTableContainer) {
    throw new Error('Device table container not found. Please ensure the device table is rendered.');
  }

  // Create a wrapper div with header
  const wrapper = document.createElement('div');
  wrapper.style.cssText = `
    background: white;
    padding: 20px;
    gap: 50px;
    font-family: DM Sans, sans-serif;
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 700px;
  `;

  // Main title
  const title = document.createElement('h2');
  title.textContent = `${deviceData.deviceName}`;
  title.style.cssText = `
    font-size: 24px;
    font-weight: bold;
    color: #363636;
    margin: 0 0 10px 0;
  `;


  const deviceTableClone = deviceTableContainer.cloneNode(true) as HTMLElement;
  deviceTableClone.style.cssText = `
    width: 100%;
    min-width: 1500px;  
    min-height: 500px;
  `;

  // Assemble the wrapper
  wrapper.appendChild(title);
  wrapper.appendChild(deviceTableClone);
  // Temporarily add to DOM for capture
  wrapper.style.position = 'absolute';
  wrapper.style.left = '-9999px';
  wrapper.style.top = '0';
  document.body.appendChild(wrapper);

  try {
    // Use html2canvas to capture the wrapper with header
    const canvas = await html2canvas(wrapper, {
      backgroundColor: '#ffffff',
      scale: 2, // Higher resolution
      useCORS: true,
      allowTaint: true,
      width: wrapper.offsetWidth,
      height: wrapper.offsetHeight
    });

    return canvas;
  } finally {
    // Clean up
    document.body.removeChild(wrapper);
  }
}


export interface GetDeviceSensorDataRequest {
  DeviceId: number;
  FromDate: string;
  ToDate: string;
}

// Export interfaces
export interface ExportDeviceDataRequest {
  deviceId: number | null;
  fromDate: string | null; // DateTimeOffset as ISO string
  toDate: string | null;   // DateTimeOffset as ISO string
  fileType: string; // 'pdf', 'excel', 'csv', 'png'
  isHeatMap: boolean;
  serialNumber: string | null;
  deviceName: string | null;
  userTimeZoneId: string;
}

// Export interfaces
export interface ExportStatisticalReportDataRequest {
  deviceIds: number[];
  fromDate: string; // DateTimeOffset as ISO string
  toDate: string;   // DateTimeOffset as ISO string
  fileType: string; // 'pdf', 'excel', 'csv', 'png'
  userTimeZoneId: string;
}

export interface ExportDeviceDataResponse {
  blob: Blob;
  fileName: string;
  success: boolean;
  message: string;
}

export interface StatisticalReportRequest {
  deviceIds: number[];
  fromDate: string;
  toDate: string;
}

// Date formatting options
const options: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

//send wifi credentials request
export interface SendWifiCredentialsRequest {
  ssid: string;
  password: string;
  deviceid: string;
  devicePassword: string;
  offsets: Params[];
  serverUrl: string;
  gateWayKey: string;
  enablebms: boolean;
  enablebacnet: boolean;
  bacnetDeviceId?: number;
  bacnetDeviceName?: string;
  bacnetPort?: number;
  modbusPort?: number;
  modbusDeviceId?: number;
  modbusConnectionType?: 'TCP' | 'RTU';
  enablemqtt: boolean;
  mqttHost?: string;
  mqttPort?: number;
  mqttSsl?: boolean;
  timestamp: number;
}

export interface Params {
  param: string,
  x: number | string,
  y: number | string
}

// Device API service
export const deviceApi = {
  // Create a new device
  createDevice: async (deviceData: CreateDeviceRequest): Promise<CreateDeviceResponse> => {

    // Get user id from local storage
    const userDataString = localStorage.getItem('userData');

    if (!userDataString) {
      throw new Error('User not authenticated. Please log in again.');
    }

    const userData = JSON.parse(userDataString);
    const userId = userData.userId;

    // Validate userId exists
    if (!userId) {
      throw new Error('User session invalid. Please log in again.');
    }

    const newformData = new FormData();
    newformData.append("UserId", userId.toString());
    newformData.append("DeviceTypeId", deviceData.DeviceTypeId.toString());
    newformData.append("DeviceName", deviceData.DeviceName || '');
    newformData.append("SerialNumber", deviceData.SerialNumber || '');
    newformData.append("DeviceLocationType", deviceData.DeviceLocationType || 'indoor');
    newformData.append("InstallationLocation", deviceData.InstallationLocation || '');
    // newformData.append("Sector", deviceData.Sector.toString());
    newformData.append("Description", deviceData.Description || '');

    // Only send Latitude/Longitude if they have valid values
    if (deviceData.Latitude !== null && deviceData.Latitude !== undefined) {
      newformData.append("Latitude", deviceData.Latitude.toString());
    }
    if (deviceData.Longitude !== null && deviceData.Longitude !== undefined) {
      newformData.append("Longitude", deviceData.Longitude.toString());
    }

    // Handle DeviceImage - send null if no file
    if (deviceData.DeviceImage) {
      newformData.append("DeviceImage", deviceData.DeviceImage as File);
    } else {
      newformData.append("DeviceImage", "");
    }

    // Handle DeviceImageUrl - send null if no URL
    if (deviceData.DeviceImageUrl) {
      newformData.append("DeviceImageUrl", deviceData.DeviceImageUrl);
    } else {
      newformData.append("DeviceImageUrl", "");
    }

    if (deviceData.EnvironmentMode) {
      newformData.append("EnvironmentMode", deviceData.EnvironmentMode);
    } else {
      newformData.append("EnvironmentMode", "");
    }

    if (deviceData.SiteId !== null && deviceData.SiteId !== undefined) {
      newformData.append("SiteId", deviceData.SiteId.toString());
    }

    return deviceRequest<CreateDeviceResponse>(ENDPOINTS.ADD_DEVICE, {
      method: 'POST',
      body: newformData,
      // Remove Content-Type header - browser will set it automatically for FormData
    });
  },

  // Get all devces with optional filtering and pagination
  getDevices: async (filters?: DeviceFilter): Promise<DeviceListResponse> => {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }

    return deviceRequest<DeviceListResponse>(`${ENDPOINTS.GET_DEVICES}?${params.toString()}`);
  },

  // Get all devices for a user
  getUserDevices: async (): Promise<Device[]> => {
    return deviceRequest<Device[]>(ENDPOINTS.GET_USER_DEVICES);
  },

  // Get a single device by ID
  getDeviceById: async (id: string): Promise<Device> => {
    const endpoint = ENDPOINTS.GET_DEVICE_DETAILS.replace(':id', id);
    return deviceRequest<Device>(endpoint);
  },

  // Get a single device by serial number
  getDeviceBySerialNumber: async (serialNumber: string): Promise<Device> => {
    const endpoint = ENDPOINTS.GET_DEVICE_BY_SERIAL.replace(':serialNumber', encodeURIComponent(serialNumber));
    return deviceRequest<Device>(endpoint);
  },

  // Get device sensor data by device ID
  getDeviceSensorData: async (request: GetDeviceSensorDataRequest): Promise<DeviceSensorData> => {
    const endpoint = `${ENDPOINTS.GET_DEVICE_SENSOR_DATA}`;
    return deviceRequest<DeviceSensorData>(endpoint, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  // Get device sensor data for detailed overview
  getDeviceSensorDataForDetailedOverview: async (request: GetDeviceSensorDataRequest): Promise<DeviceSensorData> => {
    const endpoint = `${ENDPOINTS.GET_DEVICE_SENSOR_DATA}`;
    return deviceRequest<DeviceSensorData>(endpoint, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  getDeviceHeatmapData: async (deviceId: number,
    fromDate: string,
    toDate: string
  ): Promise<DeviceDataHeatMap[]> => {
    const params = new URLSearchParams({
      DeviceId: deviceId.toString(),
      FromDate: fromDate,
      ToDate: toDate,
    });
    const endpoint = `${ENDPOINTS.GET_DEVICE_HEATMAP_DATA}?${params.toString()}`;
    return deviceRequest<DeviceDataHeatMap[]>(endpoint);
  },

  getDeviceHeatmapDataMinMax: async (deviceId: number,
    fromDate: string,
    toDate: string
  ): Promise<DeviceDataHeatMapMinMax> => {
    const params = new URLSearchParams({
      DeviceId: deviceId.toString(),
      FromDate: fromDate,
      ToDate: toDate,
    });
    const endpoint = `${ENDPOINTS.GET_DEVICE_HEATMAP_DATA_MINMAX}?${params.toString()}`;
    return deviceRequest<DeviceDataHeatMapMinMax>(endpoint);
  },

  // Update an existing device
  updateDevice: async (id: string, deviceData: UpdateDeviceRequest): Promise<any> => {
    const formData = new FormData();

    // Add all device data to form data
    Object.entries(deviceData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (key === 'DeviceImage' && value instanceof File) {
          formData.append('DeviceImage', value);
        } else if (key === 'DeviceImageUrl') {
          // Handle DeviceImageUrl - send null if no URL
          formData.append('DeviceImageUrl', value || "");
        } else {
          formData.append(key, String(value));
        }
      }
    });

    const endpoint = ENDPOINTS.UPDATE_DEVICE.replace(':id', id);
    return deviceRequest<any>(endpoint, {
      method: 'PUT',
      body: formData,
      // Remove Content-Type header - browser will set it automatically for FormData
    });
  },

  // update device status by serial number
  updateDeviceStatusBySerialNumber: async (deviceData: UpdateDeviceStatusRequest): Promise<any> => {
    const endpoint = ENDPOINTS.UPDATE_DEVICE_STATUS_BY_SERIAL_NUMBER;
    return deviceRequest<any>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(deviceData),
      // Remove Content-Type header - browser will set it automatically for FormData
    });
  },

  // Delete a device
  deleteDevice: async (id: string): Promise<any> => {
    const endpoint = ENDPOINTS.DELETE_DEVICE.replace(':id', id);
    return deviceRequest(endpoint, {
      method: 'DELETE',
    });
  },

  // Get devices for dashboard
  getDevicesForDashboard: async (): Promise<DeviceDashboardDto[]> => {
    return deviceRequest<DeviceDashboardDto[]>(ENDPOINTS.GET_DEVICES_FOR_DASHBOARD);
  },

  // Get demo device
  getDemoDevice: async (): Promise<DeviceDashboardDto> => {
    return deviceRequest<DeviceDashboardDto>(ENDPOINTS.GET_DEMO_DEVICE);
  },

  // Delete any alerts / share links that reference the demo device.
  // Called once per session after we detect the user has 1+ real devices.
  deleteDemoDeviceAlertsAndShareLinks: async (): Promise<void> => {
    return deviceRequest<void>(ENDPOINTS.DELETE_DEMO_DEVICE_ALERTS_AND_SHARE_LINKS, {
      method: 'DELETE',
    });
  },

  // Get parameters for dashboard
  getParametersForDashboard: async (): Promise<ParameterDtoPreferred[]> => {
    return deviceRequest<ParameterDtoPreferred[]>(ENDPOINTS.GET_PARAMETERS_FOR_DASHBOARD);
  },

  // Get all pollutant parameters
  getPollutantParameters: async (): Promise<PollutantParameter[]> => {
    return deviceRequest<PollutantParameter[]>(ENDPOINTS.GET_POLLUTANT_PARAMETERS);
  },

  // Get specific pollutant parameter by ID
  getPollutantParameterById: async (id: number): Promise<PollutantParameter> => {
    const endpoint = ENDPOINTS.GET_POLLUTANT_PARAMETER_BY_ID.replace(':id', id.toString());
    return deviceRequest<PollutantParameter>(endpoint);
  },

  // Get Device offsets data by device ID
  getDeviceOffsetsById: async (id: string): Promise<DeviceOffsetsData[]> => {
    const endpoint = ENDPOINTS.GET_DEVICE_OFFSETS.replace(':id', id.toString());
    return deviceRequest<DeviceOffsetsData[]>(endpoint);
  },

  // Update device offsets data 
  updateDeviceOffsetsData: async (id: string, deviceOffsetsData: UpdateDeviceOffsetsDataRequest[]): Promise<boolean> => {

    const endpoint = ENDPOINTS.UPDATE_DEVICE_OFFSETS.replace(':id', id);
    return deviceRequest<any>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(deviceOffsetsData),
    });
  },

  // Get statistical report data
  getStatisticalReportData: async (request: StatisticalReportRequest): Promise<DeviceSensorData[]> => {
    const endpoint = `${ENDPOINTS.GET_STATISTICAL_REPORT_DATA}`;
    return deviceRequest<DeviceSensorData[]>(endpoint, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  // Export statistical reports data - Backend returns file directly
  exportStatisticalReportData: async (request: ExportStatisticalReportDataRequest): Promise<ExportDeviceDataResponse> => {
    let isRefreshing = false;
    try {
      // Add authorization header if token exists
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Debug logging
      console.log('Export API Request:', {
        url: `${API_BASE_URL}${ENDPOINTS.EXPORT_STATISTICAL_REPORTS_DATA}`,
        method: 'POST',
        headers,
        body: request,
      });

      // Make HTTP request
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.EXPORT_STATISTICAL_REPORTS_DATA}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
        credentials: 'include',
        mode: 'cors',
      });

      // Handle 401 Unauthorized - clear tokens and redirect to login
      if (response.status === 401) {
        if (!isRefreshing) {
          isRefreshing = true;

          try {
            const refreshResult = await apiService.refreshAccessToken();
            if (refreshResult.success) {
              // Retry the original request with new token
              deviceApi.exportStatisticalReportData(request);
            } else {
              // Refresh failed, clear tokens and redirect to login
              // This handles account deletion from another device/session
              handleUnauthorized('API Service');
              const result: any = {
                success: false,
                message: 'Session expired. Please login again.',
                error: 'Session expired. Please login again.',
                data: null,
              }
              return result;
            }
          } catch (error) {
            // Account deleted or token invalid - clear everything and redirect
            handleUnauthorized('API Service');
            const result: any = {
              success: false,
              error: 'Authentication failed. Please login again.'
            };
            return result;
          } finally {
            isRefreshing = false;
          }
        }
      }

      // Debug logging
      console.log('Export API Response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Export API Error Response:', errorData);
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Get the blob from response
      const blob = await response.blob();

      return {
        blob,
        fileName : 'Statistical Reports Data',
        success : true,
        message : 'Statistical Reports downloaded succesfully'
      };
    } catch (error) {
      throw new Error(`Export API request failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Get all device types
  getDeviceTypes: async (): Promise<DeviceType[]> => {
    return deviceRequest<DeviceType[]>(ENDPOINTS.GET_DEVICE_TYPES);
  },

  // Bulk operations
  bulkUpdateDevices: async (deviceIds: string[], updates: Partial<UpdateDeviceRequest>): Promise<Device[]> => {
    return deviceRequest<Device[]>(`${ENDPOINTS.GET_DEVICES}/bulk-update`, {
      method: 'PATCH',
      body: JSON.stringify({
        deviceIds,
        updates,
      }),
    });
  },

  bulkDeleteDevices: async (deviceIds: string[]): Promise<void> => {
    await deviceRequest(`${ENDPOINTS.GET_DEVICES}/bulk-delete`, {
      method: 'POST',
      body: JSON.stringify({ deviceIds }),
    });
  },

  // Export device data - Backend returns file directly
  exportDeviceData: async (request: ExportDeviceDataRequest): Promise<ExportDeviceDataResponse> => {
    let isRefreshing = false;
    try {
      // Add authorization header if token exists
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Debug logging
      console.log('Export API Request:', {
        url: `${API_BASE_URL}${ENDPOINTS.EXPORT_DEVICE_DATA}`,
        method: 'POST',
        headers,
        body: request,
      });

      // Make HTTP request
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.EXPORT_DEVICE_DATA}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
        credentials: 'include',
        mode: 'cors',
      });

      // Handle 401 Unauthorized - clear tokens and redirect to login
      if (response.status === 401) {
        if (!isRefreshing) {
          isRefreshing = true;

          try {
            const refreshResult = await apiService.refreshAccessToken();
            if (refreshResult.success) {
              // Retry the original request with new token
              deviceApi.exportDeviceData(request);
            } else {
              // Refresh failed, clear tokens and redirect to login
              // This handles account deletion from another device/session
              handleUnauthorized('API Service');
              const result: any = {
                success: false,
                message: 'Session expired. Please login again.',
                error: 'Session expired. Please login again.',
                data: null,
              }
              return result;
            }
          } catch (error) {
            // Account deleted or token invalid - clear everything and redirect
            handleUnauthorized('API Service');
            const result: any = {
              success: false,
              error: 'Authentication failed. Please login again.'
            };
            return result;
          } finally {
            isRefreshing = false;
          }
        }
      }

      // Debug logging
      console.log('Export API Response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Export API Error Response:', errorData);
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Get the blob from response
      const blob = await response.blob();

      // Extract filename from Content-Disposition header or generate one
      const fileName = `${request.deviceName}_${request.serialNumber}_${new Date().toISOString().split('T')[0]}${request.fileType == 'excel' ? '.xlsx' : '.csv'}`;

      return {
        blob,
        fileName,
        success : true,
        message : 'Device data report downloaded successfully'
      };
    } catch (error) {
      throw new Error(`Export API request failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Client-side PDF generation - same as image but converted to PDF
  exportToPDF: async (request: ExportDeviceDataRequest): Promise<ExportDeviceDataResponse> => {
    try {
      let canvas: HTMLCanvasElement;
      // Use the same chart capture method as image export
      if (request.fromDate === null && request.toDate === null) {
        canvas = await captureDeviceTableAsImage(request);
      }
      else {
        canvas = await captureChartAsImage(request);
      }

      // Convert canvas to PDF
      const fileName = `${request.deviceName}${request.serialNumber !== null ? '_' + request.serialNumber : ""}_${new Date().toISOString().split('T')[0]}.pdf`;
      const blob = await canvasToPDF(canvas, fileName);

      return {
        blob,
        fileName,
        success: true,
        message : 'Device data report downloaded successfully'
      };
    } catch (error) {
      throw new Error(`PDF export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Client-side PNG generation - captures actual chart
  exportToImage: async (request: ExportDeviceDataRequest): Promise<ExportDeviceDataResponse> => {
    try {

      let canvas: HTMLCanvasElement;

      try {
        // Try to capture the actual chart from the DOM
        if (request.fromDate === null && request.toDate === null) {
          canvas = await captureDeviceTableAsImage(request);
        }
        else {
          canvas = await captureChartAsImage(request);
        }
      } catch (chartError) {
        throw new Error('Failed to capture chart');
      }

      // Convert canvas to blob
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) {
            const fileName = `${request.deviceName}${request.serialNumber !== null ? '_' + request.serialNumber : ""}_chart_${new Date().toISOString().split('T')[0]}.png`;
            resolve({
              blob,
              fileName,
              success : true,
              message : 'Device data report downloaded successfully'
            });
          } else {
            reject(new Error('Failed to generate image'));
          }
        }, 'image/png');
      });
    } catch (error) {
      throw new Error(`Image export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  //export dashboard data
  exportDashboardData: async (fileType: string, userTimeZoneId: string): Promise<ExportDeviceDataResponse> => {
    let isRefreshing = false;
    try {
      // Add authorization header if token exists
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Debug logging
      console.log('Export API Request:', {
        url: `${API_BASE_URL}${ENDPOINTS.EXPORT_DASHBOARD_DATA}`,
        method: 'POST',
        headers,
        body: { fileType: fileType, userTimeZoneId: userTimeZoneId },
      });

      // Make HTTP request
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.EXPORT_DASHBOARD_DATA}?fileType=${fileType}&userTimeZoneId=${userTimeZoneId}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        mode: 'cors',
      });

      // Handle 401 Unauthorized - clear tokens and redirect to login
      if (response.status === 401) {
        if (!isRefreshing) {
          isRefreshing = true;

          try {
            const refreshResult = await apiService.refreshAccessToken();
            if (refreshResult.success) {
              // Retry the original request with new token
              deviceApi.exportDashboardData(fileType, userTimeZoneId);
            } else {
              // Refresh failed, clear tokens and redirect to login
              // This handles account deletion from another device/session
              handleUnauthorized('API Service');
              const result: any = {
                success: false,
                message: 'Session expired. Please login again.',
                error: 'Session expired. Please login again.',
                data: null,
              }
              return result;
            }
          } catch (error) {
            // Account deleted or token invalid - clear everything and redirect
            handleUnauthorized('API Service');
            const result: any = {
              success: false,
              error: 'Authentication failed. Please login again.'
            };
            return result;
          } finally {
            isRefreshing = false;
          }
        }
      }

      // Debug logging
      console.log('Export API Response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Export API Error Response:', errorData);
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Get the blob from response
      const blob = await response.blob();

      // Extract filename from Content-Disposition header or generate one
      const fileName = `All Devices_${new Date().toISOString().split('T')[0]}${fileType == 'excel' ? '.xlsx' : '.csv'}`;

      console.log('Export API Success Response:', { fileName, blobSize: blob.size });

      return {
        blob,
        fileName,
        success : true,
        message : 'Device data report downloaded successfully'
      };
    } catch (error) {
      throw new Error(`Export API request failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  //Get aqi info data based on aqi standard
  getAqiInfoData: async (): Promise<AqiInfoData> => {
    const endpoint = `${ENDPOINTS.GET_AQI_INFO_DATA}`;
    return deviceRequest<AqiInfoData>(endpoint);
  },


  // Download file helper.
  // Delegates to the cross-platform downloadBlob so exports also save correctly
  // inside the React Native WebView (anchor-download is ignored there).
  downloadFile: async (blob: Blob, fileName: string): Promise<void> => {
    try {
      // Prefer the blob's own type; fall back to a sensible type from the extension.
      const mimeType = blob.type || mimeTypeForFileName(fileName);
      await downloadBlob(blob, fileName, mimeType);
    } catch (error) {
      throw new Error(`Download failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Log wifi connection request/response to backend
  logWifiConnection: async (data: {
    deviceId: string;
    timestamp: number;
    request: string;
    response: string;
    attemptNumber: number;
  }): Promise<any> => {
    return deviceRequest<any>(ENDPOINTS.LOG_WIFI_CONNECTION, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Send wifi credentials — legacy direct-fetch path. Kept as a fallback /
  // reference; the active SendWifiCredentials below uses the RN bridge because
  // Android marks the ESP32 AP as "no internet" and aborts WebView fetches
  // (AbortError / Timeout: true / Network: false).
  SendWifiCredentials_old: async (request: SendWifiCredentialsRequest): Promise<any> => {
    const endpoint = process.env.REACT_APP_DEVICE_WIFI_URL || 'http://192.168.4.1/wifi';
    const MAX_ATTEMPTS = 3;
    const REQUEST_TIMEOUT = 30000; // 10 seconds

    let attempt = 1;

    const delay = (ms: number) =>
      new Promise(resolve => setTimeout(resolve, ms));

    // Get user-friendly message based on HTTP status code (ESP32 only returns codes)
    const getStatusMessage = (status: number): string => {
      if (status === 200) return 'Device connected successfully';
      if (status === 400) return 'Invalid request. Please check the WiFi credentials.';
      if (status === 401) return 'Authentication failed. Please verify device credentials.';
      if (status === 404) return 'Device endpoint not found. Please check the device URL.';
      if (status === 500) return 'Device internal error. Please try again.';
      if (status >= 400 && status < 500) return `Client error (${status}). Please check your request.`;
      if (status >= 500) return `Server error (${status}). Device encountered an error.`;
      return `Unexpected status code: ${status}`;
    };

    // Build a copy of the request with the WiFi password masked. Anything
    // we surface to the user (alerts, future logs) must use this — we never
    // want a real password sitting on a screenshot a tester sends us.
   // const sanitizedRequest = {
    //  ...request,
     // password: request.password ? `***redacted (len=${request.password.length})***` : '',
   // };
    //const requestPreview = JSON.stringify(sanitizedRequest, null, 2);

    const callWithRetry = async (): Promise<any> => {
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), REQUEST_TIMEOUT);

      try {
        console.log(`SendWifiCredentials: Attempt ${attempt}/${MAX_ATTEMPTS} to ${endpoint}`);

        const response = await fetch(endpoint, {
          method: 'POST',
          body: JSON.stringify(request),
          signal: abortController.signal,
        });

        clearTimeout(timeoutId);
        const statusCode = response.status;

        // Read the response body once and reuse it for both the alert and
        // any future code that needs it. fetch bodies are streams — reading
        // here means we don't fight a `bodyUsed` error later.
        let responseBody: any = null;
        try {
          responseBody = await response.json();
        } catch {
          responseBody = null;
        }

        // Show the user exactly what was sent and what came back. On
        // Android WebView this surfaces as the system alert dialog, which
        // is the only reliable way to display content while the phone is
        // still on the device's WiFi AP.
       /* try {
          window.alert(
            `Attempt ${attempt}/${MAX_ATTEMPTS}\n\n` +
            `── REQUEST ──\n` +
            `POST ${endpoint}\n` +
            `${requestPreview}\n\n` +
            `── RESPONSE ──\n` +
            `Status: ${statusCode} ${response.statusText || ''}\n` +
            `OK: ${response.ok}\n` +
            `Body: ${responseBody ? JSON.stringify(responseBody) : '(empty)'}`
          );
        } catch {*/ /* alert can throw inside some sandboxed WebViews — never block the flow *//* } */

        // Log wifi connection to backend (fire-and-forget)
        deviceApi.logWifiConnection({
          deviceId: request.deviceid,
          timestamp: request.timestamp,
          request: JSON.stringify({ ...request, offsets: undefined, password: undefined }),
          response: JSON.stringify({ status: statusCode, ok: response.ok, body: responseBody }),
          attemptNumber: attempt,
        }).catch(() => {});

        // HTTP errors take precedence over the internetAvailable flag —
        // a 4xx/5xx means the device never even got far enough to test
        // its uplink, so reporting "no internet" would be misleading.
        if (!response.ok) {
          console.warn(`SendWifiCredentials: HTTP ${statusCode} on attempt ${attempt}`);

          // Don't retry client errors (4xx) - they indicate invalid requests
          if (statusCode >= 400 && statusCode < 500) {
            return {
              success: false,
              message: getStatusMessage(statusCode),
              error: `HTTP ${statusCode}`
            };
          }

          // Retry server errors (5xx) and other errors
          throw new Error(`HTTP ${statusCode}`);
        }

        // 2xx response — now interpret internetAvailable. Compare strictly
        // against false so a missing field (older firmware) doesn't get
        // mistaken for a failure.
        if (responseBody?.internetAvailable === false) {
          console.log(`SendWifiCredentials: failed on attempt ${attempt} - Status ${statusCode} and internet is not available`);

          // Release the Wi-Fi binding so the phone reconnects to its
          // internet network instead of staying on the device hotspot.
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({ action: 'RELEASE_WIFI_BINDING' })
            );
          }

          return {
            success: false,
            message: 'Internet is not available. Please check the WiFi details and try again.',
          };
        }

        console.log(`SendWifiCredentials: Success on attempt ${attempt} - Status ${statusCode}`);
        return {
          success: true,
          message: getStatusMessage(statusCode)
        };

      } catch (error) {
        clearTimeout(timeoutId);

        // Check if it's a timeout
        const isTimeout = error instanceof Error &&
          (error.name === 'AbortError' || error.message.includes('aborted'));

        // Check if it's a network error
        const isNetworkError = error instanceof Error &&
          (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'));

        console.error(`SendWifiCredentials: Attempt ${attempt}/${MAX_ATTEMPTS} failed`, {
          error: error instanceof Error ? error.message : 'Unknown error',
          isTimeout,
          isNetworkError
        });

        // Show the user the request that was sent and the error we got
        // back. The previous alert (HTTP-level) only fires when fetch
        // resolves; this one covers timeouts and network failures.
        // try {
        //   window.alert(
        //     `Attempt ${attempt}/${MAX_ATTEMPTS} — ERROR\n\n` +
        //     `── REQUEST ──\n` +
        //     `POST ${endpoint}\n` +
        //     `${requestPreview}\n\n` +
        //     `── ERROR ──\n` +
        //     `Name: ${error instanceof Error ? error.name : '-'}\n` +
        //     `Message: ${error instanceof Error ? error.message : String(error)}\n` +
        //     `Timeout: ${isTimeout}\n` +
        //     `Network: ${isNetworkError}`
        //   );
        // } catch { /* see note above */ }

        if (attempt >= MAX_ATTEMPTS) {
          let message = 'Failed to connect to device. Please check the connection and try again.';
          if (isTimeout) {
            message = 'Device did not respond within the timeout period. Please check the connection.';
          } else if (isNetworkError) {
            message = 'Unable to connect to device. Please ensure the device is powered on and on the same network.';
          }

          return {
            success: false,
            message,
            error: error instanceof Error ? error.message : 'Unknown error'
          };
        }

        // Fixed delay: 5 seconds before retry
        const delayMs = 10000;
        console.log(`SendWifiCredentials: Retrying in ${delayMs}ms...`);
        attempt++;
        await delay(delayMs);
        return callWithRetry();
      }
    };

   return callWithRetry();

  },

  // Web/desktop direct send. The browser POSTs the credentials straight to the
  // device SoftAP at http://192.168.4.1/wifi while the laptop is manually
  // connected to the device hotspot — there is no RN bridge on web. Mirrors
  // SendWifiCredentials_old but drops the RN RELEASE_WIFI_BINDING branch (a
  // no-op outside the mobile app). NOTE: an HTTPS-served dashboard will block
  // this as mixed content — see the web onboarding flow for the http-origin
  // caveat.
  SendWifiCredentialsDirect: async (request: SendWifiCredentialsRequest): Promise<{ success: boolean; message: string; error?: string }> => {
    const endpoint = process.env.REACT_APP_DEVICE_WIFI_URL || 'http://192.168.4.1/wifi';
    const MAX_ATTEMPTS = 5;
    const REQUEST_TIMEOUT = 60000;//60 seconds
    const RETRY_DELAY_MS = 10000;//10 seconds

    let attempt = 1;

    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const getStatusMessage = (status: number): string => {
      if (status === 200) return 'Device connected successfully';
      if (status === 400) return 'Invalid request. Please check the WiFi credentials.';
      if (status === 401) return 'Authentication failed. Please verify device credentials.';
      if (status === 404) return 'Device endpoint not found. Please check the device URL.';
      if (status === 500) return 'Device internal error. Please try again.';
      if (status >= 400 && status < 500) return `Client error (${status}). Please check your request.`;
      if (status >= 500) return `Server error (${status}). Device encountered an error.`;
      return `Unexpected status code: ${status}`;
    };

    const callWithRetry = async (): Promise<{ success: boolean; message: string; error?: string }> => {
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), REQUEST_TIMEOUT);

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          body: JSON.stringify(request),
          signal: abortController.signal,
        });
        clearTimeout(timeoutId);
        const statusCode = response.status;

        let responseBody: any = null;
        try {
          responseBody = await response.json();
        } catch {
          responseBody = null;
        }

        // Fire-and-forget connection log (password never included).
        deviceApi
          .logWifiConnection({
            deviceId: request.deviceid,
            timestamp: request.timestamp,
            request: JSON.stringify({ ...request, offsets: undefined, password: undefined }),
            response: JSON.stringify({ status: statusCode, ok: response.ok, body: responseBody }),
            attemptNumber: attempt,
          })
          .catch(() => {});

        if (!response.ok) {
          // Don't retry client errors (4xx).
          if (statusCode >= 400 && statusCode < 500) {
            return { success: false, message: getStatusMessage(statusCode), error: `HTTP ${statusCode}` };
          }
          throw new Error(`HTTP ${statusCode}`);
        }

        if (responseBody?.internetAvailable === false) {
          return {
            success: false,
            message: 'Internet is not available. Please check the WiFi details and try again.',
          };
        }

        return { success: true, message: getStatusMessage(statusCode) };
      } catch (error) {
        clearTimeout(timeoutId);

        const isTimeout =
          error instanceof Error && (error.name === 'AbortError' || error.message.includes('aborted'));
        const isNetworkError =
          error instanceof Error &&
          (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'));

        if (attempt >= MAX_ATTEMPTS) {
          let message = 'Failed to connect to device. Please check the connection and try again.';
          if (isTimeout) {
            message = 'Device did not respond within the timeout period. Please check the connection.';
          } else if (isNetworkError) {
            message =
              'Unable to connect to device. Please ensure you are connected to the device IAQ_ network and try again.';
          }
          return { success: false, message, error: error instanceof Error ? error.message : 'Unknown error' };
        }

        attempt++;
        await delay(RETRY_DELAY_MS);
        return callWithRetry();
      }
    };

    return callWithRetry();
  },

  // Send wifi credentials via the React Native bridge. The RN host receives
  // an ESP32_REQUEST postMessage, calls WifiManager.forceWifiUsage(true),
  // performs the fetch natively, then dispatches an ESP32_RESPONSE
  // CustomEvent back into the WebView with { requestId, success, data | error }.
  SendWifiCredentials: async (request: SendWifiCredentialsRequest): Promise<any> => {
    const BRIDGE_TIMEOUT = 15000;

    return new Promise((resolve) => {
      const rnBridge = (window as any).ReactNativeWebView;
      if (!rnBridge || typeof rnBridge.postMessage !== 'function') {
        resolve({
          success: false,
          message: 'Device pairing is only supported inside the SmartEco mobile app.',
          error: 'ReactNativeWebView bridge unavailable',
        });
        return;
      }

      const requestId = Date.now().toString();

      const handler = (event: Event) => {
        const detail = (event as CustomEvent).detail;
        if (!detail || detail.requestId !== requestId) return;
        window.removeEventListener('ESP32_RESPONSE', handler);
        clearTimeout(timeoutId);

        if (detail.success) {
          resolve({
            success: true,
            message: 'Device connected successfully',
            data: detail.data,
          });
        } else {
          resolve({
            success: false,
            message: detail.error || 'Failed to send Wi-Fi details to the device.',
            error: detail.error,
          });
        }
      };

      window.addEventListener('ESP32_RESPONSE', handler);

      rnBridge.postMessage(JSON.stringify({
        type: 'ESP32_REQUEST',
        requestId,
        payload: request,
      }));

      const timeoutId: ReturnType<typeof setTimeout> = setTimeout(() => {
        window.removeEventListener('ESP32_RESPONSE', handler);
        resolve({
          success: false,
          message: 'Device did not respond within the timeout period. Please check the connection.',
          error: 'Timed out waiting for ESP32',
        });
      }, BRIDGE_TIMEOUT);
    });
  }

};

export default deviceApi;
